import { useCallback, useEffect, useMemo, useState } from 'react'

import { Badge } from '../components/ui/badge'
import { Button } from '../components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog'
import { Input } from '../components/ui/input'
import {
  cancelStaffStoreOrder,
  changeStoreOrderFulfillment,
  collectStoreOrderCash,
  collectStoreOrderCashAdministratively,
  completeStaffStoreOrder,
  finishStaffStoreOrder,
  formatStoreOrderDate,
  formatStoreOrderMoney,
  getOrderStatusBadgeClass,
  getPaymentStatusBadgeClass,
  getStoreOrderEventLabel,
  getStoreOrderPaymentTypeLabel,
  getStaffOrderStatusLabel,
  getStaffStoreOrder,
  getStaffStoreOrdersProblem,
  listDeliveryUsers,
  type DeliveryUser,
  type StaffStoreOrderDetail,
  type StoreOrderReservation,
  PAYMENT_STATUS_OPTIONS,
  updateStoreOrderPaymentStatus,
} from '../services/store-orders.service'
import { useSessionStore } from '../store/auth/auth.store'

const TRANSITION_LABELS: Record<string, string> = {
  PREPARING: 'Iniciar preparación',
  READY_FOR_PICKUP: 'Marcar listo para recoger',
  ASSIGNED: 'Asignar repartidor',
  OUT_FOR_DELIVERY: 'Marcar en ruta',
  DELIVERED: 'Marcar entregado',
}

interface StoreOrderDetailProps {
  orderNumber: string
  onOrderUpdated?: () => void
}

export function StoreOrderDetail({
  orderNumber,
  onOrderUpdated,
}: StoreOrderDetailProps) {
  const canChangeStatus = useSessionStore((state) =>
    state.canAction('ecommerce.order.status'),
  )
  const canAssign = useSessionStore((state) =>
    state.canAction('ecommerce.order.assign'),
  )
  const canCancel = useSessionStore((state) =>
    state.canAction('ecommerce.order.cancel'),
  )
  const canComplete = useSessionStore((state) =>
    state.canAction('ecommerce.order.complete'),
  )
  const canFinalize = useSessionStore((state) =>
    state.canAction('ecommerce.order.finalize'),
  )
  const canCollectCash = useSessionStore((state) =>
    Boolean(state.permissions.ecommerce?.['order.cash']?.collect),
  )
  const canAdminCollectCash = useSessionStore((state) =>
    Boolean(state.permissions.ecommerce?.['order.cash']?.collect_admin),
  )
  const currentUserId = useSessionStore((state) => state.user?.id)

  const [order, setOrder] = useState<StaffStoreOrderDetail | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState('')
  const [notFound, setNotFound] = useState(false)
  const [actionError, setActionError] = useState('')
  const [activeAction, setActiveAction] = useState('')
  const [deliveryUsers, setDeliveryUsers] = useState<DeliveryUser[]>([])
  const [deliveryUsersLoading, setDeliveryUsersLoading] = useState(false)
  const [deliveryUserId, setDeliveryUserId] = useState('')
  const [showCancellation, setShowCancellation] = useState(false)
  const [cancellationReason, setCancellationReason] = useState('')
  const [cancellationNotes, setCancellationNotes] = useState('')
  const [showCompletion, setShowCompletion] = useState(false)
  const [showFinish, setShowFinish] = useState(false)
  const [showCashCollection, setShowCashCollection] = useState(false)
  const [deliveryCashReceived, setDeliveryCashReceived] = useState('')
  const [deliveryCashReason, setDeliveryCashReason] = useState('')
  const [pendingTransition, setPendingTransition] = useState<string | null>(
    null,
  )
  const [showPaymentStatusEdit, setShowPaymentStatusEdit] = useState(false)
  const [newPaymentStatus, setNewPaymentStatus] = useState('')
  const [savingPaymentStatus, setSavingPaymentStatus] = useState(false)

  const loadOrder = useCallback(async () => {
    if (!orderNumber) return
    setIsLoading(true)
    setError('')
    setNotFound(false)
    try {
      const result = await getStaffStoreOrder(orderNumber)
      setOrder(result)
      setDeliveryUserId(
        result.fulfillment?.assigned_to_user_id?.toString() || '',
      )
    } catch (loadError) {
      const problem = getStaffStoreOrdersProblem(
        loadError,
        'No pudimos cargar el pedido. Intenta nuevamente.',
      )
      setNotFound(problem.status === 404)
      setError(
        problem.status === 404
          ? 'No encontramos el pedido solicitado.'
          : problem.message,
      )
    } finally {
      setIsLoading(false)
    }
  }, [orderNumber])

  useEffect(() => {
    void loadOrder()
  }, [loadOrder])

  const canManageAssignment = useMemo(() => {
    if (!order || order.fulfillment_type !== 'DELIVERY') return false
    return (
      order.available_fulfillment_transitions.includes('ASSIGNED') ||
      order.fulfillment_status === 'ASSIGNED'
    )
  }, [order])

  useEffect(() => {
    if (!order || !canChangeStatus || !canAssign || !canManageAssignment) return
    const sucursalId = order.sucursal?.id || order.fulfillment?.sucursal?.id
    if (!sucursalId) return

    let cancelled = false
    setDeliveryUsersLoading(true)
    listDeliveryUsers(sucursalId)
      .then((users) => {
        if (!cancelled) setDeliveryUsers(users)
      })
      .catch((loadError) => {
        if (!cancelled) {
          setActionError(
            getStaffStoreOrdersProblem(
              loadError,
              'No pudimos cargar los repartidores disponibles.',
            ).message,
          )
        }
      })
      .finally(() => {
        if (!cancelled) setDeliveryUsersLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [canAssign, canChangeStatus, canManageAssignment, order])

  const updateFulfillment = async (
    status: string,
    assignedToUserId?: number,
  ) => {
    if (!order) return false
    setActiveAction(status)
    setActionError('')
    try {
      const updated = await changeStoreOrderFulfillment(order.order_number, {
        status,
        ...(assignedToUserId ? { assigned_to_user_id: assignedToUserId } : {}),
      })
      setOrder(updated)
      setDeliveryUserId(
        updated.fulfillment?.assigned_to_user_id?.toString() || '',
      )
      onOrderUpdated?.()
      return true
    } catch (saveError) {
      setActionError(
        getStaffStoreOrdersProblem(
          saveError,
          'No pudimos actualizar el estado del pedido.',
        ).message,
      )
      return false
    } finally {
      setActiveAction('')
    }
  }

  const confirmTransition = async () => {
    if (!pendingTransition) return
    const updated = await updateFulfillment(pendingTransition)
    if (updated) setPendingTransition(null)
  }

  const assignDeliveryUser = () => {
    const userId = Number(deliveryUserId)
    if (!userId) {
      setActionError('Selecciona un repartidor antes de continuar.')
      return
    }
    void updateFulfillment('ASSIGNED', userId)
  }

  const submitCancellation = async () => {
    if (!order) return
    const reason = cancellationReason.trim()
    const notes = cancellationNotes.trim()
    if (reason.length < 5 || reason.length > 500) {
      setActionError('El motivo debe tener entre 5 y 500 caracteres.')
      return
    }
    if (notes.length > 2000) {
      setActionError('Las notas no pueden superar 2000 caracteres.')
      return
    }

    setActiveAction('CANCEL')
    setActionError('')
    try {
      const updated = await cancelStaffStoreOrder(order.order_number, {
        reason,
        ...(notes ? { notes } : {}),
      })
      setOrder(updated)
      setShowCancellation(false)
      setCancellationReason('')
      setCancellationNotes('')
      onOrderUpdated?.()
    } catch (saveError) {
      setActionError(
        getStaffStoreOrdersProblem(saveError, 'No pudimos cancelar el pedido.')
          .message,
      )
    } finally {
      setActiveAction('')
    }
  }

  const submitCompletion = async () => {
    if (!order) return
    setActiveAction('COMPLETE')
    setActionError('')
    try {
      const updated = await completeStaffStoreOrder(order.order_number)
      setOrder(updated)
      setShowCompletion(false)
      onOrderUpdated?.()
    } catch (saveError) {
      setActionError(
        getStaffStoreOrdersProblem(
          saveError,
          'No pudimos completar la venta del pedido.',
        ).message,
      )
    } finally {
      setActiveAction('')
    }
  }

  const submitFinish = async () => {
    if (!order) return
    setActiveAction('FINISH')
    setActionError('')
    try {
      const updated = await finishStaffStoreOrder(order.order_number)
      setOrder(updated)
      setShowFinish(false)
      onOrderUpdated?.()
    } catch (saveError) {
      setActionError(
        getStaffStoreOrdersProblem(saveError, 'No pudimos cerrar el pedido.')
          .message,
      )
    } finally {
      setActiveAction('')
    }
  }

  const submitCashCollection = async () => {
    if (!order) return
    const cashDue =
      order.sale?.payments?.find((payment) => payment.method === 'cash')
        ?.amount || order.amounts.external_due_amount
    if (!isEnoughCash(deliveryCashReceived, cashDue)) {
      setActionError('El efectivo recibido debe cubrir el saldo del pedido.')
      return
    }

    setActiveAction('COLLECT_CASH')
    setActionError('')
    try {
      if (adminCashCollection) {
        await collectStoreOrderCashAdministratively(order.order_number, {
          cash_received_amount: deliveryCashReceived,
          reason: deliveryCashReason.trim(),
        })
      } else {
        await collectStoreOrderCash(order.order_number, {
          cash_received_amount: deliveryCashReceived,
        })
      }
      await loadOrder()
      setShowCashCollection(false)
      setDeliveryCashReceived('')
      setDeliveryCashReason('')
      onOrderUpdated?.()
    } catch (saveError) {
      setActionError(
        getStaffStoreOrdersProblem(
          saveError,
          'No pudimos registrar el cobro del pedido.',
        ).message,
      )
    } finally {
      setActiveAction('')
    }
  }

  const submitPaymentStatusChange = async () => {
    if (!order || !newPaymentStatus) return
    setSavingPaymentStatus(true)
    setActionError('')
    try {
      const updated = await updateStoreOrderPaymentStatus(order.order_number, newPaymentStatus)
      setOrder(updated)
      setShowPaymentStatusEdit(false)
      setNewPaymentStatus('')
      onOrderUpdated?.()
    } catch (saveError) {
      setActionError(
        getStaffStoreOrdersProblem(saveError, 'No pudimos actualizar el estado de pago.').message,
      )
    } finally {
      setSavingPaymentStatus(false)
    }
  }

  if (isLoading) {
    return (
      <div className="space-y-4 py-4">
        <div className="bg-muted h-8 w-72 animate-pulse rounded" />
        <div className="bg-muted h-48 animate-pulse rounded-xl" />
        <div className="bg-muted h-64 animate-pulse rounded-xl" />
      </div>
    )
  }

  if (!order || error) {
    return (
      <div className="bg-card rounded-xl border p-6 text-center shadow-sm">
        <h1 className="text-foreground text-xl font-semibold">
          {notFound
            ? 'Pedido no encontrado'
            : 'No fue posible cargar el pedido'}
        </h1>
        <p className="text-muted-foreground mt-2 text-sm">{error}</p>
        {!notFound && (
          <div className="mt-5 flex justify-center">
            <button
              type="button"
              onClick={() => void loadOrder()}
              className="bg-primary text-primary-foreground rounded-md px-4 py-2 text-sm font-medium"
            >
              Reintentar
            </button>
          </div>
        )}
      </div>
    )
  }

  const placed = formatStoreOrderDate(order.created_at)
  const fulfillment = order.fulfillment
  const address = fulfillment?.delivery_address
  const normalTransitions = order.available_fulfillment_transitions.filter(
    (status) => status !== 'ASSIGNED',
  )
  const cashPayment = order.payments.find((payment) => payment.type === 'CASH')
  const discountAmount = Number(order.amounts.discount_total_amount)
  const taxAmount = Number(order.amounts.tax_total_amount)
  const deliveryFeeAmount = Number(order.amounts.delivery_fee_amount)
  const savingsAmount = Number(order.amounts.savings_total_amount)
  const loyaltyCreditAmount = Number(
    order.amounts.loyalty_credit_applied_amount,
  )
  const externalDueAmount = Number(order.amounts.external_due_amount)
  const hasLoyaltyCredit = loyaltyCreditAmount > 0
  const externalAmountLabel =
    order.payment_status === 'PAID'
      ? 'Pagado con otro método'
      : 'Restante por cobrar'
  const readyToComplete =
    !order.sale &&
    !order.loyalty_hold_block_reason &&
    !order.cancellations?.some(
      (cancellation) => cancellation.status === 'REQUESTED',
    ) &&
    ((order.fulfillment_type === 'PICKUP' &&
      order.fulfillment_status === 'READY_FOR_PICKUP') ||
      (order.fulfillment_type === 'DELIVERY' &&
        order.fulfillment_status === 'ASSIGNED'))
  const readyToFinish =
    Boolean(order.sale) &&
    order.status === 'COMPLETED' &&
    !order.cancellations?.some(
      (cancellation) => cancellation.status === 'REQUESTED',
    ) &&
    ((order.fulfillment_type === 'PICKUP' &&
      order.fulfillment_status === 'READY_FOR_PICKUP') ||
      (order.fulfillment_type === 'DELIVERY' &&
        order.fulfillment_status === 'DELIVERED'))
  const deliveryCashPending =
    order.fulfillment_type === 'DELIVERY' && cashPayment?.status === 'PENDING'
  const courierCashCollection =
    canCollectCash && fulfillment?.assigned_to_user_id === currentUserId
  const adminCashCollection = !courierCashCollection && canAdminCollectCash
  const canCollectThisOrder =
    (courierCashCollection || adminCashCollection) &&
    deliveryCashPending &&
    !order.cash_collection &&
    order.fulfillment_status === 'DELIVERED'
  const completionBlockedByCash = cashPayment?.status === 'PENDING'
  const deliveryCashDue =
    order.sale?.payments?.find((payment) => payment.method === 'cash')
      ?.amount || order.amounts.external_due_amount
  const canSubmitCashCollection = isEnoughCash(
    deliveryCashReceived,
    deliveryCashDue,
  ) && (!adminCashCollection || deliveryCashReason.trim().length >= 10)

  const fulfillmentDetails: Array<[string, string]> = [
    [
      'Modalidad',
      order.fulfillment_type === 'DELIVERY'
        ? 'Entrega a domicilio'
        : 'Recolección en sucursal',
    ],
    ['Estado', getFulfillmentStatusLabel(order)],
  ]

  if (order.fulfillment_type === 'DELIVERY') {
    fulfillmentDetails.push(
      ['Repartidor', fulfillment?.assigned_user_name || 'Sin asignar'],
      ['Cobertura', fulfillment?.coverage?.name || 'Sin cobertura registrada'],
    )
    if (fulfillment?.assigned_at) {
      fulfillmentDetails.push([
        'Repartidor asignado',
        formatDateTime(fulfillment.assigned_at),
      ])
    }
    if (fulfillment?.started_at) {
      fulfillmentDetails.push([
        'Salida a entrega',
        formatDateTime(fulfillment.started_at),
      ])
    }
    if (fulfillment?.delivered_at) {
      fulfillmentDetails.push([
        'Entregado',
        formatDateTime(fulfillment.delivered_at),
      ])
    }
  } else {
    if (fulfillment?.ready_at) {
      fulfillmentDetails.push([
        'Listo para recoger desde',
        formatDateTime(fulfillment.ready_at),
      ])
    }
    if (order.pickup_recipient_name) {
      fulfillmentDetails.push([
        'Persona que recogió',
        order.pickup_recipient_name,
      ])
    }
  }

  if (order.completed_at) {
    fulfillmentDetails.push([
      order.fulfillment_type === 'PICKUP'
        ? 'Recolección completada'
        : 'Pedido completado',
      formatDateTime(order.completed_at),
    ])
  }
  if (fulfillment?.cancelled_at) {
    fulfillmentDetails.push([
      'Entrega cancelada',
      formatDateTime(fulfillment.cancelled_at),
    ])
  }

  return (
    <div className="space-y-6">
      {/* Header — fecha + estado de pedido + estado de pago */}
      <div className="flex flex-col gap-3 rounded-xl border bg-card p-4 shadow-sm sm:flex-row sm:items-center sm:justify-between sm:p-5">
        <div>
          <p className="text-xs font-medium uppercase text-muted-foreground">Pedido</p>
          <p className="mt-0.5 text-base font-bold text-foreground">{order.order_number}</p>
          <p className="mt-1 text-sm text-muted-foreground">
            {placed.date} &nbsp;·&nbsp; {placed.time}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Badge className={getOrderStatusBadgeClass(order.fulfillment_status || order.status)}>
            {getFulfillmentStatusLabel(order)}
          </Badge>
          <Badge className={getPaymentStatusBadgeClass(order.payment_status)}>
            {getStaffOrderStatusLabel(order.payment_status)}
          </Badge>
        </div>
      </div>

      {order.is_simulated && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
          Este es un pedido de prueba. No genera una venta real ni movimientos
          reales de inventario o caja.
        </div>
      )}

      {actionError && !showCashCollection && !showCompletion && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          {actionError}
        </div>
      )}

      {order.loyalty_hold_block_reason && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
          La retención histórica de lealtad requiere revisión. Este pedido no
          puede completarse hasta que se corrija.
        </div>
      )}

      {order.fulfillment_type === 'DELIVERY' &&
        order.fulfillment_status === 'DELIVERED' &&
        deliveryCashPending &&
        !order.cash_collection && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
            La entrega ya está marcada como completada. El repartidor asignado
            debe registrar el cobro al cliente antes de cerrar el pedido. La
            rendición de ese efectivo a caja es un paso posterior.
          </div>
        )}

      {(canChangeStatus ||
        (canCancel && order.can_cancel) ||
        (canComplete && readyToComplete) ||
        (canFinalize && readyToFinish) ||
        canCollectThisOrder) && (
          <section className="bg-card rounded-xl border p-4 shadow-sm sm:p-5">
            <h2 className="text-foreground font-semibold">Acciones del pedido</h2>
            <p className="text-muted-foreground mt-1 text-sm">
              Sólo se muestran las acciones permitidas para el estado actual. Los
              cambios de estado deben confirmarse y no pueden revertirse desde
              esta pantalla.
            </p>

            {canChangeStatus && normalTransitions.length > 0 && (
              <div className="mt-4 flex flex-wrap gap-2">
                {normalTransitions.map((status) => (
                  <button
                    key={status}
                    type="button"
                    disabled={Boolean(activeAction)}
                    onClick={() => {
                      setActionError('')
                      setPendingTransition(status)
                    }}
                    className="bg-primary text-primary-foreground rounded-md px-4 py-2 text-sm font-medium disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {activeAction === status
                      ? 'Guardando...'
                      : TRANSITION_LABELS[status] ||
                      `Cambiar a ${getStaffOrderStatusLabel(status)}`}
                  </button>
                ))}
              </div>
            )}

            {canChangeStatus && canAssign && canManageAssignment && (
              <div className="mt-4 grid gap-2 sm:grid-cols-[minmax(0,1fr)_auto]">
                <label className="min-w-0 text-sm font-medium">
                  Repartidor
                  <select
                    value={deliveryUserId}
                    onChange={(event) => setDeliveryUserId(event.target.value)}
                    disabled={deliveryUsersLoading || Boolean(activeAction)}
                    className="bg-background mt-1 block w-full rounded-md border px-3 py-2 font-normal"
                  >
                    <option value="">
                      {deliveryUsersLoading
                        ? 'Cargando repartidores...'
                        : 'Selecciona un repartidor'}
                    </option>
                    {deliveryUsers.map((user) => (
                      <option key={user.id} value={user.id}>
                        {[user.name, user.last_name].filter(Boolean).join(' ')}
                      </option>
                    ))}
                  </select>
                </label>
                <button
                  type="button"
                  disabled={deliveryUsersLoading || Boolean(activeAction)}
                  onClick={assignDeliveryUser}
                  className="border-primary text-primary self-end rounded-md border px-4 py-2 text-sm font-medium disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {activeAction === 'ASSIGNED'
                    ? 'Guardando...'
                    : order.fulfillment_status === 'ASSIGNED'
                      ? 'Reasignar'
                      : 'Asignar'}
                </button>
              </div>
            )}

            {canCollectThisOrder && (
              <div className="mt-4 border-t pt-4">
                <button
                  type="button"
                  disabled={Boolean(activeAction)}
                  onClick={() => {
                    setActionError('')
                    setDeliveryCashReceived('')
                    setDeliveryCashReason('')
                    setShowCashCollection(true)
                  }}
                  className="bg-primary text-primary-foreground rounded-md px-4 py-2 text-sm font-medium disabled:opacity-60"
                >
                  {adminCashCollection
                    ? 'Registrar cobro informado por repartidor'
                    : 'Registrar cobro de entrega'}
                </button>
              </div>
            )}

            {canComplete && readyToComplete && (
              <div className="mt-4 border-t pt-4">
                <button
                  type="button"
                  disabled={Boolean(activeAction)}
                  onClick={() => {
                    setActionError('')
                    setShowCompletion(true)
                  }}
                  className="bg-primary text-primary-foreground rounded-md px-4 py-2 text-sm font-medium disabled:cursor-not-allowed disabled:opacity-60"
                >
                  Completar venta
                </button>
              </div>
            )}

            {canFinalize && readyToFinish && (
              <div className="mt-4 border-t pt-4">
                <button
                  type="button"
                  disabled={
                    Boolean(activeAction) || Boolean(completionBlockedByCash)
                  }
                  onClick={() => {
                    setActionError('')
                    setShowFinish(true)
                  }}
                  className="bg-primary text-primary-foreground rounded-md px-4 py-2 text-sm font-medium disabled:opacity-60"
                >
                  Completar pedido
                </button>
                {completionBlockedByCash && (
                  <p className="mt-2 text-sm text-amber-700">
                    Acredita primero el efectivo del pedido.
                  </p>
                )}
              </div>
            )}

            {canCancel && order.can_cancel && (
              <div className="mt-4 border-t pt-4">
                {!showCancellation ? (
                  <button
                    type="button"
                    onClick={() => {
                      setShowCancellation(true)
                      setActionError('')
                    }}
                    className="rounded-md border border-red-300 px-4 py-2 text-sm font-medium text-red-700"
                  >
                    Cancelar pedido
                  </button>
                ) : (
                  <div className="max-w-2xl space-y-3">
                    <label className="block text-sm font-medium">
                      Motivo de cancelación{' '}
                      <span className="text-red-600">*</span>
                      <textarea
                        value={cancellationReason}
                        onChange={(event) =>
                          setCancellationReason(event.target.value)
                        }
                        maxLength={500}
                        rows={3}
                        className="bg-background mt-1 block w-full rounded-md border px-3 py-2 font-normal"
                      />
                    </label>
                    <label className="block text-sm font-medium">
                      Notas adicionales
                      <textarea
                        value={cancellationNotes}
                        onChange={(event) =>
                          setCancellationNotes(event.target.value)
                        }
                        maxLength={2000}
                        rows={3}
                        className="bg-background mt-1 block w-full rounded-md border px-3 py-2 font-normal"
                      />
                    </label>
                    <div className="flex flex-col-reverse gap-2 sm:flex-row">
                      <button
                        type="button"
                        disabled={Boolean(activeAction)}
                        onClick={() => setShowCancellation(false)}
                        className="rounded-md border px-4 py-2 text-sm font-medium"
                      >
                        Conservar pedido
                      </button>
                      <button
                        type="button"
                        disabled={Boolean(activeAction)}
                        onClick={() => void submitCancellation()}
                        className="rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white disabled:opacity-60"
                      >
                        {activeAction === 'CANCEL'
                          ? 'Cancelando...'
                          : 'Confirmar cancelación'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </section>
        )}

      <div className="grid gap-6 xl:grid-cols-3">
        <div className="space-y-6 xl:col-span-2">
          <InfoSection title="Cliente">
            <InfoGrid
              items={[
                ['Nombre', order.customer.name],
                ['Teléfono', order.customer.phone || 'No registrado'],
                ['Correo', order.customer.email || 'No registrado'],
                ['Sucursal', order.sucursal?.name || 'No disponible'],
                ['Estado del pedido', getStaffOrderStatusLabel(order.status)],
                [
                  'Estado del pago',
                  getStaffOrderStatusLabel(order.payment_status),
                ],
              ]}
            />
          </InfoSection>

          <InfoSection title="Entrega">
            <InfoGrid items={fulfillmentDetails} />
            {order.fulfillment_type === 'DELIVERY' && (
              <div className="bg-muted/50 mt-4 rounded-lg p-4 text-sm">
                <p className="font-medium">Dirección de entrega</p>
                <p className="text-muted-foreground mt-1 break-words">
                  {address
                    ? [
                      `${address.street} ${address.exterior_number}`,
                      address.interior_number
                        ? `Int. ${address.interior_number}`
                        : '',
                      address.neighborhood,
                      address.city,
                      address.state,
                      `C.P. ${address.postal_code}`,
                    ]
                      .filter(Boolean)
                      .join(', ')
                    : 'Sin dirección registrada'}
                </p>
                {address?.references && (
                  <p className="text-muted-foreground mt-2 break-words">
                    Referencias: {address.references}
                  </p>
                )}
                {address?.contact_phone && (
                  <p className="text-muted-foreground mt-2 break-words">
                    Teléfono de contacto: {address.contact_phone}
                  </p>
                )}
              </div>
            )}
          </InfoSection>

          <InfoSection title="Productos">
            <div className="divide-y">
              {order.items.map((item, index) => (
                <div
                  key={`${item.product_id}-${index}`}
                  className="py-4 first:pt-0 last:pb-0"
                >
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex min-w-0 items-center gap-3">
                      {item.product.image_url ? (
                        <img
                          src={item.product.image_url}
                          alt={item.product.name}
                          className="h-14 w-14 shrink-0 rounded-md border object-contain"
                        />
                      ) : (
                        <div className="bg-muted text-muted-foreground flex h-14 w-14 shrink-0 items-center justify-center rounded-md border">
                          <svg
                            className="h-6 w-6"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            aria-hidden="true"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={1.5}
                              d="m21 16-4 4-4-4m4 4V4M3 8l4-4 4 4M7 4v16"
                            />
                          </svg>
                        </div>
                      )}
                      <div className="min-w-0">
                        <p className="text-sm font-medium break-words">
                          {item.product.name}
                        </p>
                        <p className="text-muted-foreground text-xs break-words">
                          Cantidad: {item.quantity}
                          {item.product.unit
                            ? ` · ${item.product.unit.name}`
                            : ''}
                          {item.product.sku
                            ? ` · SKU: ${item.product.sku}`
                            : ''}
                          {item.product.barcode
                            ? ` · Código: ${item.product.barcode}`
                            : ''}
                        </p>
                      </div>
                    </div>
                    <p className="shrink-0 text-sm font-semibold">
                      {formatStoreOrderMoney(
                        item.amounts.total_amount,
                        order.currency_code,
                      )}
                    </p>
                  </div>
                  <div className="bg-muted/40 mt-3 grid gap-3 rounded-md p-3 text-xs sm:grid-cols-2 lg:grid-cols-4">
                    <DetailValue
                      label="Precio unitario"
                      value={formatStoreOrderMoney(
                        item.amounts.applied_unit_total_amount,
                        order.currency_code,
                      )}
                    />
                    <DetailValue
                      label="Subtotal"
                      value={formatStoreOrderMoney(
                        item.amounts.subtotal_amount,
                        order.currency_code,
                      )}
                    />
                    <DetailValue
                      label="Descuento"
                      value={formatStoreOrderMoney(
                        item.amounts.discount_amount,
                        order.currency_code,
                      )}
                    />
                    <DetailValue
                      label="Impuestos"
                      value={formatStoreOrderMoney(
                        item.amounts.tax_amount,
                        order.currency_code,
                      )}
                    />
                  </div>
                </div>
              ))}
            </div>
          </InfoSection>

          <InfoSection title="Pagos">
            {/* Resumen de estado de pago con acción de edición */}
            <div className="mb-4 flex items-center justify-between gap-3 rounded-lg border p-3">
              <div>
                <p className="text-xs font-medium uppercase text-muted-foreground">Estado de pago</p>
                <div className="mt-1 flex items-center gap-2">
                  <Badge className={getPaymentStatusBadgeClass(order.payment_status)}>
                    {getStaffOrderStatusLabel(order.payment_status)}
                  </Badge>
                </div>
              </div>
              {canChangeStatus && !order.payments.some((payment) => payment.type === 'PAYMENT_GATEWAY') && (
                <button
                  type="button"
                  onClick={() => {
                    setNewPaymentStatus(order.payment_status)
                    setActionError('')
                    setShowPaymentStatusEdit(true)
                  }}
                  className="shrink-0 rounded-md border border-input px-3 py-1.5 text-xs font-medium hover:bg-muted"
                >
                  Editar
                </button>
              )}
            </div>

            {order.payments?.length === 0 ? (
              <p className="text-muted-foreground text-sm">Sin pagos registrados.</p>
            ) : (
              <div className="divide-y">
                {order.payments.map((payment, index) => (
                  <div
                    key={`${payment.type}-${index}`}
                    className="flex flex-col gap-2 py-3 first:pt-0 last:pb-0 sm:flex-row sm:items-start sm:justify-between"
                  >
                    <div className="space-y-1">
                      <p className="text-sm font-semibold">
                        {payment.method.name || payment.type}
                      </p>
                      <div className="flex flex-wrap items-center gap-1.5">
                        <Badge className={getPaymentStatusBadgeClass(payment.status)}>
                          {getStaffOrderStatusLabel(payment.status)}
                        </Badge>
                        <span className="text-xs text-muted-foreground">
                          {getStoreOrderPaymentTypeLabel(payment.type)}
                        </span>
                        {payment.metadata?.transaction_id && (
                          <Badge variant="outline" className='w-full'>
                            ID Transacción: {payment.metadata?.transaction_id}
                          </Badge>
                        )}
                      </div>
                      {payment.paid_at && (
                        <p className="text-muted-foreground text-xs">
                          Pagado: {formatDateTime(payment.paid_at)}
                        </p>
                      )}
                      {payment.cancelled_at && (
                        <p className="text-muted-foreground text-xs">
                          Cancelado: {formatDateTime(payment.cancelled_at)}
                        </p>
                      )}
                      {payment.cash_tendered_amount && (
                        <p className="text-muted-foreground text-xs">
                          Recibido:{' '}
                          {formatStoreOrderMoney(payment.cash_tendered_amount, order.currency_code)}
                          {payment.expected_change_amount
                            ? ` · Cambio: ${formatStoreOrderMoney(payment.expected_change_amount, order.currency_code)}`
                            : ''}
                        </p>
                      )}
                    </div>
                    <p className="shrink-0 text-sm font-bold">
                      {formatStoreOrderMoney(payment.amount, order.currency_code)}
                    </p>
                  </div>
                ))}
              </div>
            )}
            {order.payment_checkout && (
              <div className="bg-muted/40 mt-4 rounded-md border p-3">
                <p className="text-sm font-medium">Sesión de pago en línea</p>
                <div className="mt-3 grid gap-3 text-xs sm:grid-cols-2">
                  <DetailValue
                    label="Proveedor"
                    value={
                      order.payment_checkout.provider === 'mercadopago'
                        ? 'Mercado Pago'
                        : order.payment_checkout.provider
                    }
                  />
                  <DetailValue
                    label="Estado"
                    value={getStaffOrderStatusLabel(
                      order.payment_checkout.status,
                    )}
                  />
                  {order.payment_checkout.preference_id && (
                    <DetailValue
                      label="Referencia"
                      value={order.payment_checkout.preference_id}
                    />
                  )}
                  {order.payment_checkout.expires_at && (
                    <DetailValue
                      label="Vigencia"
                      value={formatDateTime(order.payment_checkout.expires_at)}
                    />
                  )}
                </div>
              </div>
            )}
          </InfoSection>
        </div>

        <div className="space-y-6">
          <InfoSection title="Resumen">
            <AmountRow
              label="Subtotal"
              value={formatStoreOrderMoney(
                order.amounts.subtotal_amount,
                order.currency_code,
              )}
            />
            <AmountRow
              label="Descuentos"
              value={formatStoreOrderMoney(
                order.amounts.discount_total_amount,
                order.currency_code,
              )}
              sign={discountAmount > 0 ? '-' : undefined}
              tone={discountAmount > 0 ? 'negative' : 'default'}
            />
            <AmountRow
              label="Impuestos"
              value={formatStoreOrderMoney(
                order.amounts.tax_total_amount,
                order.currency_code,
              )}
              sign={taxAmount > 0 ? '+' : undefined}
            />
            <AmountRow
              label="Envío"
              value={
                deliveryFeeAmount > 0
                  ? formatStoreOrderMoney(
                    order.amounts.delivery_fee_amount,
                    order.currency_code,
                  )
                  : 'Gratis'
              }
              sign={deliveryFeeAmount > 0 ? '+' : undefined}
              tone={deliveryFeeAmount > 0 ? 'default' : 'positive'}
            />
            <AmountRow
              label="Total del pedido"
              value={formatStoreOrderMoney(
                order.amounts.total_amount,
                order.currency_code,
              )}
              strong
            />

            {savingsAmount > 0 && (
              <div className="mt-2 border-t pt-2 text-sm">
                <div className="flex items-center justify-between gap-4">
                  <span>Ahorro total de la compra</span>
                  <span className="text-right font-semibold text-green-700">
                    {formatStoreOrderMoney(
                      order.amounts.savings_total_amount,
                      order.currency_code,
                    )}
                  </span>
                </div>
              </div>
            )}

            {hasLoyaltyCredit && (
              <div className="mt-3 border-t pt-3">
                <p className="text-muted-foreground mb-1 text-xs font-medium tracking-wide uppercase">
                  Forma de pago
                </p>
                <AmountRow
                  label="Saldo de lealtad aplicado"
                  value={formatStoreOrderMoney(
                    order.amounts.loyalty_credit_applied_amount,
                    order.currency_code,
                  )}
                  sign="-"
                  tone="loyalty"
                />
                <div className="mt-2 flex items-center justify-between gap-4 border-t pt-3 text-sm font-semibold">
                  <span>
                    {externalDueAmount > 0
                      ? externalAmountLabel
                      : 'Cubierto completamente con lealtad'}
                  </span>
                  <span className="text-right text-green-700">
                    {formatStoreOrderMoney(
                      order.amounts.external_due_amount,
                      order.currency_code,
                    )}
                  </span>
                </div>
              </div>
            )}
          </InfoSection>

          {(order.loyalty || order.loyalty_hold) && (
            <InfoSection title="Lealtad">
              <InfoGrid items={getLoyaltyDetails(order)} />
            </InfoSection>
          )}

          {order.sale && (
            <InfoSection title="Venta generada">
              <InfoGrid
                items={[
                  ['Ticket', order.sale.ticket_number],
                  ['Estado', getStaffOrderStatusLabel(order.sale.status)],
                  ['Ticket generado', formatDateTime(order.sale.completed_at)],
                  ['Tipo', order.sale.is_simulated ? 'Simulada' : 'Venta real'],
                ]}
              />
              {!!order.sale.payments?.length && (
                <div className="mt-4 space-y-2">
                  <p className="text-sm font-medium">Pagos de la venta</p>
                  {order.sale.payments.map((payment) => (
                    <div
                      key={payment.id}
                      className="bg-muted/50 rounded-md p-3 text-sm"
                    >
                      <p>
                        {getStoreOrderPaymentTypeLabel(
                          payment.method === 'cash'
                            ? 'CASH'
                            : payment.method || '',
                        )}{' '}
                        · {getStaffOrderStatusLabel(payment.status)}
                      </p>
                      <p className="text-muted-foreground">
                        {payment.original_expected_amount &&
                          payment.original_expected_amount !== payment.amount
                          ? `Original: ${formatStoreOrderMoney(payment.original_expected_amount, order.currency_code)} · `
                          : ''}
                        {payment.status === 'PENDING'
                          ? 'Saldo exigible'
                          : 'Importe'}
                        :{' '}
                        {formatStoreOrderMoney(
                          payment.amount,
                          order.currency_code,
                        )}
                      </p>
                    </div>
                  ))}
                </div>
              )}
              {!!order.sale.receivable_adjustments?.length && (
                <div className="mt-4 space-y-2">
                  <p className="text-sm font-medium">
                    Ajustes del saldo pendiente
                  </p>
                  {order.sale.receivable_adjustments.map((adjustment) => (
                    <div
                      key={adjustment.id}
                      className="bg-muted/50 rounded-md p-3 text-sm"
                    >
                      <p>
                        Devolución #{adjustment.sale_return_id}: saldo reducido
                        en{' '}
                        {formatStoreOrderMoney(
                          adjustment.amount,
                          order.currency_code,
                        )}
                      </p>
                      <p className="text-muted-foreground">
                        {formatStoreOrderMoney(
                          adjustment.balance_before,
                          order.currency_code,
                        )}{' '}
                        →{' '}
                        {formatStoreOrderMoney(
                          adjustment.balance_after,
                          order.currency_code,
                        )}
                        {' · '}
                        {formatDateTime(adjustment.occurred_at)}
                      </p>
                    </div>
                  ))}
                </div>
              )}
              {!!order.sale.cash_movements?.length && (
                <div className="mt-4 space-y-2">
                  <p className="text-sm font-medium">
                    Movimientos de caja vinculados
                  </p>
                  {order.sale.cash_movements.map((movement) => (
                    <p
                      key={movement.id}
                      className="bg-muted/50 rounded-md p-3 text-sm"
                    >
                      #{movement.id} · {getStaffOrderStatusLabel(movement.type)}{' '}
                      ·{' '}
                      {formatStoreOrderMoney(
                        movement.amount,
                        order.currency_code,
                      )}{' '}
                      · Caja sesión #{movement.cash_register_session_id} ·{' '}
                      {formatDateTime(movement.occurred_at)}
                    </p>
                  ))}
                </div>
              )}
              {!!order.sale.returns?.length && (
                <div className="mt-4 space-y-2">
                  <p className="text-sm font-medium">Devoluciones</p>
                  {order.sale.returns.map((saleReturn) => (
                    <p
                      key={saleReturn.id}
                      className="bg-muted/50 rounded-md p-3 text-sm"
                    >
                      {saleReturn.return_number} ·{' '}
                      {getStaffOrderStatusLabel(saleReturn.status)} · Total{' '}
                      {formatStoreOrderMoney(
                        saleReturn.total_refund_amount,
                        order.currency_code,
                      )}{' '}
                      · Reembolso externo{' '}
                      {formatStoreOrderMoney(
                        saleReturn.external_refund_amount,
                        order.currency_code,
                      )}
                    </p>
                  ))}
                </div>
              )}
            </InfoSection>
          )}

          {order.cash_collection && (
            <InfoSection title="Cobro domiciliario">
              <InfoGrid
                items={[
                  ['Repartidor', order.cash_collection.collector_name],
                  ['Registrado por', order.cash_collection.recorded_by_name || 'Por confirmar'],
                  ['Origen del registro', order.cash_collection.recording_method === 'ADMIN'
                    ? 'Administrativo excepcional' : 'Repartidor'],
                  [
                    'Importe del pedido',
                    formatStoreOrderMoney(
                      order.cash_collection.amount,
                      order.currency_code,
                    ),
                  ],
                  [
                    'Efectivo recibido',
                    formatStoreOrderMoney(
                      order.cash_collection.cash_received_amount,
                      order.currency_code,
                    ),
                  ],
                  [
                    'Cambio',
                    formatStoreOrderMoney(
                      order.cash_collection.change_amount,
                      order.currency_code,
                    ),
                  ],
                  [
                    'Estado',
                    getStaffOrderStatusLabel(order.cash_collection.status),
                  ],
                  [
                    'Importe rendido',
                    formatStoreOrderMoney(
                      order.cash_collection.remitted_amount,
                      order.currency_code,
                    ),
                  ],
                  [
                    'Diferencia registrada',
                    formatStoreOrderMoney(
                      order.cash_collection.shortfall_amount,
                      order.currency_code,
                    ),
                  ],
                  [
                    'Cobrado',
                    formatDateTime(order.cash_collection.collected_at),
                  ],
                  [
                    'Conciliado',
                    order.cash_collection.reconciled_at
                      ? formatDateTime(order.cash_collection.reconciled_at)
                      : 'Pendiente',
                  ],
                ]}
              />
              {order.cash_collection.administrative_reason && (
                <p className="bg-amber-50 mt-4 rounded-md p-3 text-sm text-amber-800">
                  Motivo del registro administrativo: {order.cash_collection.administrative_reason}
                </p>
              )}
              {!!order.cash_collection.remittances.length && (
                <div className="mt-4 space-y-2">
                  <p className="text-sm font-medium">Rendiciones en caja</p>
                  {order.cash_collection.remittances.map((remittance) => (
                    <p
                      key={remittance.id}
                      className="bg-muted/50 rounded-md p-3 text-sm"
                    >
                      Movimiento #{remittance.cash_register_movement_id} ·{' '}
                      {formatStoreOrderMoney(
                        remittance.amount,
                        order.currency_code,
                      )}{' '}
                      · {formatDateTime(remittance.received_at)}
                    </p>
                  ))}
                </div>
              )}
              {!!order.cash_collection.discrepancies.length && (
                <div className="mt-4 space-y-2">
                  <p className="text-sm font-medium">
                    Diferencias documentadas
                  </p>
                  {order.cash_collection.discrepancies.map((difference) => (
                    <p
                      key={difference.id}
                      className="bg-muted/50 rounded-md p-3 text-sm"
                    >
                      {formatStoreOrderMoney(
                        difference.amount,
                        order.currency_code,
                      )}{' '}
                      · {difference.reason} ·{' '}
                      {formatDateTime(difference.resolved_at)}
                    </p>
                  ))}
                </div>
              )}
            </InfoSection>
          )}

          <InfoSection title="Cancelaciones">
            {!order.cancellations?.length ? (
              <p className="text-muted-foreground text-sm">
                Sin solicitudes de cancelación.
              </p>
            ) : (
              <div className="space-y-4">
                {order.cancellations.map((cancellation) => {
                  const requested = formatStoreOrderDate(
                    cancellation.requested_at,
                  )
                  return (
                    <div
                      key={cancellation.id}
                      className="rounded-lg border p-3"
                    >
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <Badge className="bg-slate-100 text-slate-700">
                          {getCancellationStatusLabel(cancellation.status)}
                        </Badge>
                        <span className="text-muted-foreground text-xs">
                          {requested.date} {requested.time}
                        </span>
                      </div>
                      <p className="mt-2 text-sm break-words">
                        {cancellation.reason}
                      </p>
                      {cancellation.notes && (
                        <p className="text-muted-foreground mt-2 text-xs break-words">
                          Notas: {cancellation.notes}
                        </p>
                      )}
                      <p className="text-muted-foreground mt-2 text-xs break-words">
                        Solicitó: {cancellation.requester_name || 'Cliente'}
                      </p>
                      {cancellation.resolution_reason && (
                        <p className="text-muted-foreground mt-2 text-xs break-words">
                          Resolución: {cancellation.resolution_reason}
                        </p>
                      )}
                      {(cancellation.resolver_name ||
                        cancellation.resolved_at) && (
                          <p className="text-muted-foreground mt-1 text-xs break-words">
                            {cancellation.resolver_name
                              ? `Resolvió: ${cancellation.resolver_name}`
                              : 'Solicitud resuelta'}
                            {cancellation.resolved_at
                              ? ` · ${formatDateTime(cancellation.resolved_at)}`
                              : ''}
                          </p>
                        )}
                      {cancellation.approved_at && (
                        <p className="text-muted-foreground mt-1 text-xs">
                          Aprobada: {formatDateTime(cancellation.approved_at)}
                        </p>
                      )}
                      {cancellation.cancelled_at && (
                        <p className="text-muted-foreground mt-1 text-xs">
                          Cancelación completada:{' '}
                          {formatDateTime(cancellation.cancelled_at)}
                        </p>
                      )}
                    </div>
                  )
                })}
              </div>
            )}
          </InfoSection>

          <details className="bg-card rounded-xl border p-4 shadow-sm sm:p-5">
            <summary className="text-foreground cursor-pointer font-semibold">
              Historial y reservas
            </summary>
            <div className="mt-4 space-y-5">
              <div>
                <h3 className="text-sm font-medium">Eventos</h3>
                {order.events.length === 0 ? (
                  <p className="text-muted-foreground mt-1 text-sm">
                    Sin eventos registrados.
                  </p>
                ) : (
                  <ol className="mt-2 space-y-3 border-l pl-4">
                    {order.events.map((event) => {
                      const occurred = formatStoreOrderDate(event.occurred_at)

                      // Extraemos los estados de pago desde el evento o desde metadata
                      const fromPaymentStatus =
                        event.from_payment_status ??
                        event.metadata?.previous_payment_status
                      const toPaymentStatus =
                        event.to_payment_status ?? event.metadata?.new_payment_status

                      return (
                        <li key={event.id} className="text-sm">
                          <p className="font-medium break-words">
                            {getStoreOrderEventLabel(event.event_type)}
                          </p>

                          {/* 1. Estado del Pedido (Existente) */}
                          {(event.from_order_status || event.to_order_status) && (
                            <p className="text-muted-foreground text-xs">
                              Pedido:{' '}
                              {event.from_order_status
                                ? getStaffOrderStatusLabel(event.from_order_status)
                                : 'Sin estado'}{' '}
                              →{' '}
                              {event.to_order_status
                                ? getStaffOrderStatusLabel(event.to_order_status)
                                : 'Sin cambio'}
                            </p>
                          )}

                          {/* 2. Estado de Entrega (Existente) */}
                          {(event.from_fulfillment_status ||
                            event.to_fulfillment_status) && (
                              <p className="text-muted-foreground text-xs">
                                Entrega:{' '}
                                {event.from_fulfillment_status
                                  ? getStaffOrderStatusLabel(event.from_fulfillment_status)
                                  : 'Sin estado'}{' '}
                                →{' '}
                                {event.to_fulfillment_status
                                  ? getStaffOrderStatusLabel(event.to_fulfillment_status)
                                  : 'Sin cambio'}
                              </p>
                            )}

                          {/* 3. Estado de Pago (NUEVO BLOQUE) */}
                          {(fromPaymentStatus || toPaymentStatus) && (
                            <p className="text-muted-foreground text-xs">
                              Pago:{' '}
                              {fromPaymentStatus
                                ? getStaffOrderStatusLabel(fromPaymentStatus)
                                : 'Sin estado'}{' '}
                              →{' '}
                              {toPaymentStatus
                                ? getStaffOrderStatusLabel(toPaymentStatus)
                                : 'Sin cambio'}
                            </p>
                          )}

                          {/* Fecha y Actor (Existente) */}
                          <p className="text-muted-foreground text-xs">
                            {occurred.date} {occurred.time}
                            {event.actor_name ? ` · ${event.actor_name}` : ''}
                          </p>
                        </li>
                      )
                    })}
                  </ol>
                )}
              </div>
              <div>
                <h3 className="text-sm font-medium">Reservas de inventario</h3>
                <div className="mt-2 space-y-2">
                  {order.items.flatMap((item) =>
                    (item.reservations || []).map((reservation) => (
                      <div
                        key={reservation.id}
                        className="bg-muted/50 rounded-md p-3 text-xs"
                      >
                        <p className="font-medium break-words">
                          {item.product.name}
                        </p>
                        <p className="text-muted-foreground">
                          {getStaffOrderStatusLabel(reservation.status)} ·
                          Cantidad: {reservation.quantity}
                        </p>
                        <p className="text-muted-foreground break-words">
                          {reservation.batch_number_snapshot
                            ? ` · Lote: ${reservation.batch_number_snapshot}`
                            : ''}
                          {reservation.expiration_date_snapshot
                            ? ` · Caducidad: ${reservation.expiration_date_snapshot}`
                            : ''}
                        </p>
                        {getReservationDate(reservation) && (
                          <p className="text-muted-foreground mt-1">
                            {getReservationDate(reservation)}
                          </p>
                        )}
                      </div>
                    )),
                  )}
                  {!order.items.some((item) => item.reservations?.length) && (
                    <p className="text-muted-foreground text-sm">
                      Sin reservas registradas.
                    </p>
                  )}
                </div>
              </div>
            </div>
          </details>
        </div>
      </div>
      {/* ── Dialog: editar estado de pago ───────────────────────────── */}
      {showPaymentStatusEdit && (
        <Dialog
          open={showPaymentStatusEdit}
          onOpenChange={(open) => {
            if (savingPaymentStatus) return
            setShowPaymentStatusEdit(open)
            if (!open) setActionError('')
          }}
        >
          <DialogContent className="max-h-[calc(100dvh-1rem)] gap-0 overflow-y-auto p-0 sm:max-w-sm">
            <DialogHeader className="border-b px-5 py-5 pr-14 sm:px-6">
              <DialogTitle>Editar estado de pago</DialogTitle>
              <DialogDescription>
                Selecciona el nuevo estado de pago para este pedido. Esta acción queda registrada en el historial.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 px-5 py-5 sm:px-6">
              <div className="rounded-lg border p-3">
                <p className="text-xs font-medium uppercase text-muted-foreground">Estado actual</p>
                <Badge className={`mt-1.5 ${getPaymentStatusBadgeClass(order.payment_status)}`}>
                  {getStaffOrderStatusLabel(order.payment_status)}
                </Badge>
              </div>

              <label className="block text-sm font-medium">
                Nuevo estado
                <select
                  value={newPaymentStatus}
                  onChange={(e) => setNewPaymentStatus(e.target.value)}
                  className="mt-1 block w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-normal"
                >
                  <option value="">Selecciona un estado</option>
                  {PAYMENT_STATUS_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </label>

              {actionError && (
                <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                  {actionError}
                </div>
              )}
            </div>

            <DialogFooter className="bg-muted/30 border-t px-5 py-4 sm:px-6">
              <Button
                type="button"
                variant="outline"
                disabled={savingPaymentStatus}
                onClick={() => setShowPaymentStatusEdit(false)}
                className="w-full sm:w-auto"
              >
                Cancelar
              </Button>
              <Button
                type="button"
                disabled={savingPaymentStatus || !newPaymentStatus || newPaymentStatus === order.payment_status}
                onClick={() => void submitPaymentStatusChange()}
                className="w-full sm:w-auto"
              >
                {savingPaymentStatus ? 'Guardando...' : 'Guardar cambio'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
      {showCashCollection && (
        <Dialog
          open={showCashCollection}
          onOpenChange={(open) => {
            if (activeAction) return
            setShowCashCollection(open)
            if (!open) setActionError('')
          }}
        >
          <DialogContent className="max-h-[calc(100dvh-1rem)] gap-0 overflow-y-auto p-0 sm:max-w-md">
            <DialogHeader className="border-b px-5 py-5 pr-14 sm:px-6">
              <DialogTitle>{adminCashCollection
                ? 'Registrar cobro informado por repartidor'
                : 'Registrar cobro de entrega'}</DialogTitle>
              <DialogDescription>
                {adminCashCollection
                  ? 'Registro administrativo excepcional del efectivo informado por el repartidor asignado. No ingresa dinero a caja; su rendición se registra después.'
                  : 'Confirma el efectivo recibido del cliente. La entrega se registra por separado.'}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-5 px-5 py-5 sm:px-6">
              <div className="border-primary/20 bg-primary/5 rounded-lg border p-4">
                <p className="text-muted-foreground text-xs font-medium tracking-wide uppercase">
                  Saldo por cobrar
                </p>
                <p className="text-foreground mt-1 text-2xl font-semibold">
                  {formatStoreOrderMoney(deliveryCashDue, order.currency_code)}
                </p>
                <p className="text-muted-foreground mt-1 text-xs">
                  Pedido {order.order_number}
                </p>
              </div>

              <MoneyInput
                id="delivery-cash-received"
                label="Efectivo recibido"
                required
                value={deliveryCashReceived}
                onChange={(value) => {
                  setDeliveryCashReceived(value)
                  if (actionError) setActionError('')
                }}
              />

              {adminCashCollection && (
                <label className="block text-sm font-medium">
                  Motivo del registro administrativo <span className="text-red-600">*</span>
                  <textarea
                    value={deliveryCashReason}
                    onChange={(event) => setDeliveryCashReason(event.target.value)}
                    minLength={10}
                    maxLength={500}
                    rows={3}
                    className="bg-background mt-1 block w-full rounded-md border px-3 py-2 font-normal"
                    placeholder="Indica cómo se confirmó el cobro y quién lo reportó"
                  />
                </label>
              )}

              <div className="bg-muted/60 flex items-center justify-between gap-4 rounded-lg px-4 py-3 text-sm">
                <span className="text-muted-foreground">Cambio a entregar</span>
                <span className="text-foreground text-right font-semibold">
                  {formatCashChange(
                    deliveryCashReceived,
                    deliveryCashDue,
                    order.currency_code,
                  )}
                </span>
              </div>

              {actionError && (
                <div
                  role="alert"
                  className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700"
                >
                  <p className="font-medium">Revisa la información del cobro</p>
                  <p className="mt-1">{actionError}</p>
                </div>
              )}
            </div>

            <DialogFooter className="bg-muted/30 border-t px-5 py-4 sm:px-6">
              <Button
                type="button"
                variant="outline"
                disabled={Boolean(activeAction)}
                onClick={() => setShowCashCollection(false)}
                className="w-full sm:w-auto"
              >
                Cancelar
              </Button>
              <Button
                type="button"
                disabled={Boolean(activeAction) || !canSubmitCashCollection}
                onClick={() => void submitCashCollection()}
                className="w-full sm:w-auto"
              >
                {activeAction === 'COLLECT_CASH'
                  ? 'Registrando...'
                  : 'Confirmar cobro'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {showCompletion && (
        <Dialog
          open={showCompletion}
          onOpenChange={(open) => {
            if (activeAction) return
            setShowCompletion(open)
            if (!open) setActionError('')
          }}
        >
          <DialogContent className="max-h-[calc(100dvh-1rem)] gap-0 overflow-y-auto p-0 sm:max-w-md">
            <DialogHeader className="border-b px-5 py-5 pr-14 sm:px-6">
              <DialogTitle>Completar venta del pedido</DialogTitle>
              <DialogDescription>
                Se generará el ticket y se consumirán las reservas. El pedido
                seguirá pendiente de entrega y el efectivo no ingresará a caja
                todavía.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-5 px-5 py-5 sm:px-6">
              <div className="border-primary/20 bg-primary/5 rounded-lg border p-4">
                <p className="text-muted-foreground text-xs font-medium tracking-wide uppercase">
                  Total de la venta
                </p>
                <p className="text-foreground mt-1 text-2xl font-semibold">
                  {formatStoreOrderMoney(
                    order.amounts.total_amount,
                    order.currency_code,
                  )}
                </p>
                <p className="text-muted-foreground mt-1 text-xs break-all">
                  Pedido {order.order_number}
                </p>
              </div>

              {actionError && (
                <div
                  role="alert"
                  className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700"
                >
                  <p className="font-medium">
                    Revisa la información antes de continuar
                  </p>
                  <p className="mt-1">{actionError}</p>
                </div>
              )}
            </div>

            <DialogFooter className="bg-muted/30 border-t px-5 py-4 sm:px-6">
              <Button
                type="button"
                variant="outline"
                disabled={Boolean(activeAction)}
                onClick={() => setShowCompletion(false)}
                className="w-full sm:w-auto"
              >
                Cancelar
              </Button>
              <Button
                type="button"
                disabled={Boolean(activeAction)}
                onClick={() => void submitCompletion()}
                className="w-full sm:w-auto"
              >
                {activeAction === 'COMPLETE'
                  ? 'Generando ticket...'
                  : 'Generar ticket'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      <Dialog
        open={showFinish}
        onOpenChange={(open) => {
          if (!activeAction) setShowFinish(open)
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Completar pedido</DialogTitle>
            <DialogDescription>
              Confirma que el pedido fue entregado y el pago quedó acreditado.
              Esta acción no genera otro movimiento de dinero.
            </DialogDescription>
          </DialogHeader>
          {actionError && (
            <p role="alert" className="text-sm text-red-700">
              {actionError}
            </p>
          )}
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              disabled={Boolean(activeAction)}
              onClick={() => setShowFinish(false)}
            >
              Volver
            </Button>
            <Button
              type="button"
              disabled={Boolean(activeAction)}
              onClick={() => void submitFinish()}
            >
              {activeAction === 'FINISH' ? 'Cerrando...' : 'Confirmar cierre'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={pendingTransition !== null}
        onOpenChange={(open) => {
          if (!open && !activeAction) {
            setPendingTransition(null)
            setActionError('')
          }
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Confirmar cambio de estado</DialogTitle>
            <DialogDescription>
              Revisa el cambio antes de continuar. Una vez confirmado no podrás
              regresar al estado anterior desde esta pantalla.
            </DialogDescription>
          </DialogHeader>

          {pendingTransition && (
            <div className="bg-muted/50 rounded-lg border p-4 text-sm">
              <p className="text-muted-foreground">El pedido cambiará de</p>
              <p className="text-foreground mt-1 font-semibold">
                {getStaffOrderStatusLabel(order.fulfillment_status)} →{' '}
                {getStaffOrderStatusLabel(pendingTransition)}
              </p>
            </div>
          )}

          {actionError && (
            <p className="rounded-md bg-red-50 p-3 text-sm text-red-700">
              {actionError}
            </p>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              disabled={Boolean(activeAction)}
              onClick={() => {
                setPendingTransition(null)
                setActionError('')
              }}
            >
              Conservar estado actual
            </Button>
            <Button
              type="button"
              disabled={Boolean(activeAction)}
              onClick={() => void confirmTransition()}
            >
              {activeAction === pendingTransition
                ? 'Guardando...'
                : 'Confirmar cambio'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

function InfoSection({
  title,
  children,
}: {
  title: string
  children: React.ReactNode
}) {
  return (
    <section className="bg-card rounded-xl border p-4 shadow-sm sm:p-5">
      <h2 className="text-foreground mb-4 font-semibold">{title}</h2>
      {children}
    </section>
  )
}

function InfoGrid({ items }: { items: Array<[string, string]> }) {
  return (
    <dl className="grid gap-4 sm:grid-cols-2">
      {items.map(([label, value]) => (
        <div key={label} className="min-w-0">
          <dt className="text-muted-foreground text-xs">{label}</dt>
          <dd className="mt-1 text-sm font-medium break-words">{value}</dd>
        </div>
      ))}
    </dl>
  )
}

function DetailValue({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0">
      <p className="text-muted-foreground">{label}</p>
      <p className="text-foreground mt-0.5 font-medium break-words">{value}</p>
    </div>
  )
}

function getFulfillmentStatusLabel(order: StaffStoreOrderDetail) {
  if (order.fulfillment_status === 'COMPLETED') {
    return order.fulfillment_type === 'PICKUP' ? 'Recogido' : 'Entregado'
  }
  return getStaffOrderStatusLabel(order.fulfillment_status || order.status)
}

function getLoyaltyDetails(
  order: StaffStoreOrderDetail,
): Array<[string, string]> {
  const details: Array<[string, string]> = []
  const loyalty = order.loyalty

  if (loyalty?.current_tier?.name) {
    details.push(['Nivel al comprar', loyalty.current_tier.name])
  }
  if (loyalty?.points_balance != null) {
    details.push([
      'Puntos disponibles al comprar',
      loyalty.points_balance.toLocaleString('es-MX'),
    ])
  }
  if (loyalty?.reward?.points_required) {
    details.push([
      'Puntos canjeados',
      loyalty.reward.points_required.toLocaleString('es-MX'),
    ])
  }
  if (loyalty?.credit_generated && Number(loyalty.credit_generated) > 0) {
    details.push([
      'Saldo generado por recompensa',
      formatStoreOrderMoney(loyalty.credit_generated, order.currency_code),
    ])
  }
  if (loyalty?.points_to_earn != null) {
    details.push([
      'Puntos obtenidos con el pedido',
      loyalty.points_to_earn.toLocaleString('es-MX'),
    ])
  }
  if (order.loyalty_hold) {
    details.push(
      [
        'Estado de la retención',
        getStaffOrderStatusLabel(order.loyalty_hold.status),
      ],
      [
        'Puntos reservados',
        order.loyalty_hold.points_reserved.toLocaleString('es-MX'),
      ],
      [
        'Crédito reservado',
        formatStoreOrderMoney(
          order.loyalty_hold.credit_reserved_amount,
          order.currency_code,
        ),
      ],
    )
  }

  return details.length > 0
    ? details
    : [['Programa de lealtad', 'Sin beneficios aplicados']]
}

function getCancellationStatusLabel(status: string) {
  return (
    {
      REQUESTED: 'Pendiente',
      APPROVED: 'Aprobada',
      REJECTED: 'Rechazada',
      COMPLETED: 'Completada',
    }[status] || getStaffOrderStatusLabel(status)
  )
}

function getReservationDate(reservation: StoreOrderReservation) {
  if (reservation.consumed_at) {
    return `Consumida: ${formatDateTime(reservation.consumed_at)}`
  }
  if (reservation.released_at) {
    return `Liberada: ${formatDateTime(reservation.released_at)}`
  }
  if (reservation.cancelled_at) {
    return `Cancelada: ${formatDateTime(reservation.cancelled_at)}`
  }
  return ''
}

function AmountRow({
  label,
  value,
  strong = false,
  sign,
  tone = 'default',
}: {
  label: string
  value: string
  strong?: boolean
  sign?: '+' | '-'
  tone?: 'default' | 'negative' | 'positive' | 'loyalty'
}) {
  const valueTone = {
    default: '',
    negative: 'text-red-600',
    positive: 'text-green-700',
    loyalty: 'text-purple-700',
  }[tone]

  return (
    <div
      className={`flex items-center justify-between gap-4 py-2 text-sm ${strong ? 'mt-2 border-t pt-4 font-semibold' : ''
        }`}
    >
      <span>{label}</span>
      <span className={`text-right ${valueTone}`}>
        {sign}
        {value}
      </span>
    </div>
  )
}

function formatDateTime(value: string | null | undefined) {
  if (!value) return 'Por confirmar'
  const formatted = formatStoreOrderDate(value)
  return `${formatted.date}${formatted.time ? `, ${formatted.time}` : ''}`
}

function MoneyInput({
  id,
  label,
  required = false,
  value,
  onChange,
}: {
  id: string
  label: string
  required?: boolean
  value: string
  onChange: (value: string) => void
}) {
  return (
    <div className="space-y-2">
      <label htmlFor={id} className="text-sm font-medium">
        {label}{' '}
        {required && (
          <span className="text-red-600" aria-hidden="true">
            *
          </span>
        )}
      </label>
      <Input
        id={id}
        type="number"
        min="0"
        step="0.01"
        inputMode="decimal"
        placeholder="0.00"
        value={value}
        onChange={(event) => onChange(event.target.value)}
      />
    </div>
  )
}

function isEnoughCash(received: string, due: string) {
  if (!received.trim()) return false
  const receivedAmount = Number(received)
  const dueAmount = Number(due)
  return Number.isFinite(receivedAmount) && receivedAmount >= dueAmount
}

function formatCashChange(received: string, due: string, currency: string) {
  if (!received.trim()) return '—'
  const receivedAmount = Number(received)
  const dueAmount = Number(due)
  if (!Number.isFinite(receivedAmount) || !Number.isFinite(dueAmount)) {
    return 'Por confirmar'
  }
  return formatStoreOrderMoney(
    Math.max(receivedAmount - dueAmount, 0).toFixed(2),
    currency,
  )
}
